import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Quote, Star, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const GOOGLE_REVIEW_URL = "https://maps.app.goo.gl/Wzhg9jPpaPa8TEPb7";

function GoogleBadge() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-medium text-[#5C6356] bg-white border border-[#E6E2D8] rounded-full px-3 py-1">
      <svg width="12" height="12" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.4-7.5 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.5 5.5C41.4 35.4 44 30.1 44 24c0-1.3-.1-2.3-.4-3.5z"/>
      </svg>
      Google
    </span>
  );
}

export default function Testimonianze() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const showOriginalNote = i18n.resolvedLanguage !== "it";

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-testimonianze" className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="label-eyebrow mb-4">{t("testimonials.eyebrow")}</div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-[#2C2F2A]">
          {t("testimonials.title1")} <em className="text-[#8A9A86] font-light">{t("testimonials.title2")}</em>.
        </h1>
        <p className="mt-8 text-lg text-[#5C6356] leading-relaxed">{t("testimonials.intro")}</p>
        <div className="mt-6 flex items-center gap-3">
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, k) => (
              <Star key={k} size={20} fill="#C27B66" strokeWidth={0} />
            ))}
          </div>
          <span className="text-sm text-[#5C6356]">{t("testimonials.average")} <b>5.0</b> {t("testimonials.onGoogle")}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-16">
        {items.map((tt, i) => (
          <div key={tt.id} data-testid={`testimonial-${i}`} className="card-soft p-8 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <Quote className="w-8 h-8 text-[#C27B66]" strokeWidth={1} />
              <GoogleBadge />
            </div>
            {showOriginalNote && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A9A86] mb-2">🇮🇹 {t("testimonials.originalLanguageNote")}</div>
            )}
            <p className="font-serif text-lg leading-relaxed text-[#2C2F2A] italic flex-1">"{tt.text}"</p>
            <div className="mt-6 flex items-center justify-between pt-5 border-t border-[#E6E2D8]/70">
              <div>
                <div className="font-medium">{tt.name}</div>
                <div className="text-xs text-[#5C6356] mt-0.5">{tt.role}</div>
              </div>
              <div className="flex gap-0.5">
                {Array.from({ length: tt.rating }).map((_, k) => (
                  <Star key={k} size={14} fill="#C27B66" strokeWidth={0} />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-6">
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="leave-google-review"
          className="card-soft p-8 group flex items-center justify-between"
        >
          <div>
            <div className="label-eyebrow mb-2">{t("testimonials.leaveReviewEyebrow")}</div>
            <div className="font-serif text-2xl">{t("testimonials.leaveReviewTitle")}</div>
            <p className="text-sm text-[#5C6356] mt-2">{t("testimonials.leaveReviewText")}</p>
          </div>
          <ExternalLink className="text-[#8A9A86] group-hover:translate-x-1 transition-transform" size={20} strokeWidth={1.5} />
        </a>
        <div className="card-soft p-8 bg-[#F3F0EA]">
          <div className="label-eyebrow mb-2">{t("testimonials.ctaEyebrow")}</div>
          <div className="font-serif text-2xl">{t("testimonials.ctaTitle")}</div>
          <p className="text-sm text-[#5C6356] mt-2 mb-5">{t("testimonials.ctaText")}</p>
          <Link to="/prenotazioni" data-testid="testimonials-cta" className="btn-primary">{t("testimonials.ctaButton")}</Link>
        </div>
      </div>
    </div>
  );
}
